import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

// Keep in sync with MeasureReductionHandler so a read-only ring looks identical
// to the one the tool draws while the reduction is still being edited.
export const RING_LINE_WIDTH = 4.0; // px

// Object names on the two halves of an interactive section ring, so a handler
// can find the pick target / the visible line inside its own group.
export const SECTION_RING_PICK_NAME = 'SectionRing_pick';
export const SECTION_RING_LINE_NAME = 'SectionRing_line';

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _ab = new THREE.Vector3();
const _bc = new THREE.Vector3();
const _ca = new THREE.Vector3();

function worldPositionAt(attr, i, mw, out) {
  out.set(attr.getX(i), attr.getY(i), attr.getZ(i));
  return out.applyMatrix4(mw);
}

/**
 * Turn the unordered plane/triangle intersection segments into a single
 * polyline so a fat line can be drawn through them.
 *
 * `points` is the consecutive-pair list `sliceSectionAtY` produces: points[2k]
 * and points[2k+1] are the two ends of one crossing edge. Endpoints are welded
 * on a spatial hash (a point is merged with any existing node within `tol`,
 * searched over the 27 surrounding cells so a weld never fails just because two
 * coincident points straddle a cell boundary), then the ring is walked along
 * that real adjacency.
 *
 * That makes this O(n). It used to be a greedy nearest-neighbour scan that
 * re-searched every remaining point on every step — O(n^2), which on a dense
 * slice (thousands of crossings, one call per ring per rebuild) stalled the
 * main thread for seconds.
 *
 * Returns the longest component found, closed if it closes. A slice that picks
 * up a stray island therefore draws its real ring instead of one polyline
 * zig-zagging between the two.
 */
function orderLoop(points, tol = 1e-4) {
  if (!points || points.length < 2) return [];

  const cell = Math.max(tol, 1e-9);
  const tolSq = tol * tol;

  // Cell keys are a packed numeric hash rather than a `${gx}_${gy}_${gz}`
  // string: the weld probes 27 cells per point, and building that many strings
  // per point cost more than the whole quadratic scan it replaced. Two distinct
  // cells colliding on one bucket is harmless — the distance test below is what
  // decides a weld, the bucket only narrows the candidates.
  const cellKey = (gx, gy, gz) =>
    (Math.imul(gx, 73856093) ^ Math.imul(gy, 19349663) ^ Math.imul(gz, 83492791)) >>> 0;

  const cells = new Map(); // cell key -> node ids sitting in (or colliding with) that cell
  const pts = [];          // node id -> welded position
  const nbrs = [];         // node id -> neighbouring node ids

  const nodeAt = (p) => {
    const gx = Math.round(p.x / cell);
    const gy = Math.round(p.y / cell);
    const gz = Math.round(p.z / cell);
    // Fast path: the coincident endpoint the neighbouring triangle produced is
    // almost always in this very cell.
    const own = cells.get(cellKey(gx, gy, gz));
    if (own) {
      for (let i = 0; i < own.length; i++) {
        if (pts[own[i]].distanceToSquared(p) <= tolSq) return own[i];
      }
    }
    // Slow path: it can still be one cell over if the two coincident points
    // straddle a cell boundary. Skipping this would split the ring in two.
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const bucket = cells.get(cellKey(gx + dx, gy + dy, gz + dz));
          if (!bucket) continue;
          for (let i = 0; i < bucket.length; i++) {
            if (pts[bucket[i]].distanceToSquared(p) <= tolSq) return bucket[i];
          }
        }
      }
    }
    const id = pts.length;
    pts.push(p.clone());
    nbrs.push([]);
    if (own) own.push(id);
    else cells.set(cellKey(gx, gy, gz), [id]);
    return id;
  };

  const edgeStarts = [];
  for (let i = 0; i + 1 < points.length; i += 2) {
    const a = nodeAt(points[i]);
    const b = nodeAt(points[i + 1]);
    if (a === b) continue; // degenerate crossing: both ends welded together
    nbrs[a].push(b);
    nbrs[b].push(a);
    edgeStarts.push(a);
  }
  if (!edgeStarts.length) return [];

  // Numeric edge keys for the same reason as the cell keys — the walk touches
  // every edge, and string keys dominated the cost.
  const used = new Set();
  const EDGE_STRIDE = 1 << 26; // node ids never come close to this
  const edgeKey = (a, b) => (a < b ? a * EDGE_STRIDE + b : b * EDGE_STRIDE + a);

  // Follow unused edges away from `start` until the walk closes back on it or
  // dead-ends. Each step consumes one edge, so this always terminates.
  const walk = (start) => {
    const chain = [start];
    let curr = start;
    for (;;) {
      const adj = nbrs[curr];
      let next = -1;
      for (let i = 0; i < adj.length; i++) {
        if (!used.has(edgeKey(curr, adj[i]))) { next = adj[i]; break; }
      }
      if (next === -1) return { chain, closed: false };
      used.add(edgeKey(curr, next));
      chain.push(next);
      curr = next;
      if (curr === start) return { chain, closed: true };
    }
  };

  let best = [];
  for (let e = 0; e < edgeStarts.length; e++) {
    const start = edgeStarts[e];
    const fwd = walk(start);
    let chain = fwd.chain;
    if (chain.length < 2) continue;
    if (!fwd.closed) {
      // Seeded mid-chain on an open component: walk the other side too so the
      // polyline is not cut in half at the seed.
      const back = walk(start);
      if (back.chain.length > 1) chain = back.chain.slice(1).reverse().concat(chain);
    }
    if (chain.length > best.length) best = chain;
  }

  if (best.length < 2) return [];
  const loop = best.map((id) => pts[id].clone());
  if (loop[0].distanceTo(loop[loop.length - 1]) > tol) loop.push(loop[0].clone());
  return loop;
}

/**
 * Slice `mesh` with the horizontal plane at world-Y `y`.
 *
 * The raw, shared half of every section measurement in the app: the unordered
 * plane/triangle intersection segments (world space, consecutive pairs), their
 * summed length — the circumference at that height — and the slice centroid.
 * Callers turn that into a drawable ring (`buildRingAtY`,
 * `buildSectionRingObjects`) or into M-L / A-P extents (`sectionExtents`).
 *
 * @returns {{segs: THREE.Vector3[], length: number, centroid: THREE.Vector3}|null}
 *   null when the plane misses the mesh.
 */
export function sliceSectionAtY(mesh, y) {
  if (!mesh?.geometry?.attributes?.position) return null;
  mesh.updateMatrixWorld(true);

  const pos = mesh.geometry.attributes.position;
  const index = mesh.geometry.index ? mesh.geometry.index.array : null;
  const mw = mesh.matrixWorld;
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);

  const tris = index ? index.length / 3 : pos.count / 3;
  const segs = [];
  let length = 0;
  for (let t = 0; t < tris; t++) {
    const i0 = index ? index[t * 3 + 0] : t * 3 + 0;
    const i1 = index ? index[t * 3 + 1] : t * 3 + 1;
    const i2 = index ? index[t * 3 + 2] : t * 3 + 2;
    worldPositionAt(pos, i0, mw, _a);
    worldPositionAt(pos, i1, mw, _b);
    worldPositionAt(pos, i2, mw, _c);
    const d0 = plane.distanceToPoint(_a);
    const d1 = plane.distanceToPoint(_b);
    const d2 = plane.distanceToPoint(_c);
    const pair = [];
    if (Math.sign(d0) !== Math.sign(d1)) { const k = d0 / (d0 - d1); pair.push(_ab.copy(_b).sub(_a).multiplyScalar(k).add(_a).clone()); }
    if (Math.sign(d1) !== Math.sign(d2)) { const k = d1 / (d1 - d2); pair.push(_bc.copy(_c).sub(_b).multiplyScalar(k).add(_b).clone()); }
    if (Math.sign(d2) !== Math.sign(d0)) { const k = d2 / (d2 - d0); pair.push(_ca.copy(_a).sub(_c).multiplyScalar(k).add(_c).clone()); }
    if (pair.length === 2) { segs.push(pair[0], pair[1]); length += pair[0].distanceTo(pair[1]); }
  }
  if (!segs.length) return null;

  const centroid = new THREE.Vector3();
  for (let i = 0; i < segs.length; i++) centroid.add(segs[i]);
  centroid.multiplyScalar(1 / segs.length);
  centroid.y = y;

  return { segs, length, centroid };
}

/**
 * Medio-lateral / antero-posterior width of a slice, measured along the two
 * landmark axes (see `_computeAxes` in `measureHelpers`) rather than world X/Z.
 *
 * @param {{segs: THREE.Vector3[], centroid: THREE.Vector3}} section
 * @param {THREE.Vector3} uML
 * @param {THREE.Vector3} uAP
 */
export function sectionExtents(section, uML, uAP) {
  const segs = section?.segs;
  if (!segs?.length) return { medioLat: 0, antPost: 0 };
  const c = section.centroid;
  let minML = Infinity, maxML = -Infinity, minAP = Infinity, maxAP = -Infinity;
  for (let i = 0; i < segs.length; i++) {
    const x = segs[i].x - c.x;
    const z = segs[i].z - c.z;
    const sML = x * uML.x + z * uML.z;
    const sAP = x * uAP.x + z * uAP.z;
    if (sML < minML) minML = sML;
    if (sML > maxML) maxML = sML;
    if (sAP < minAP) minAP = sAP;
    if (sAP > maxAP) maxAP = sAP;
  }
  return { medioLat: (maxML - minML) || 0, antPost: (maxAP - minAP) || 0 };
}

function ringLineMaterial(color, renderer, width = RING_LINE_WIDTH) {
  // depthTest:true so the socket occludes the far half of the ring;
  // polygonOffset keeps the visible half off the coincident surface.
  const mat = new LineMaterial({
    color,
    linewidth: width,
    transparent: true,
    opacity: 0.98,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  const el = renderer?.domElement;
  if (el) mat.resolution.set(el.clientWidth, el.clientHeight);
  return mat;
}

/**
 * Drawable objects for a slice, in coordinates RELATIVE TO `origin` so they can
 * be parented to a group standing there and dragged as one.
 *
 * `origin` defaults to the slice centroid (where a freshly built ring's group is
 * placed). Pass the group's current position instead while a drag is in flight:
 * the transform gizmo rewrites the group's X/Z from the drag start every frame,
 * so a ring rebuilt around the new centroid would sit off the surface.
 *
 * `thin` is a near-invisible raw-segment `LineSegments` used only as a raycast
 * target (a fat line is not reliably pickable); `fat` is the visible ring.
 *
 * @returns {{thin: THREE.LineSegments, fat: import('three/examples/jsm/lines/Line2.js').Line2|null}}
 */
export function buildSectionRingObjects(section, color, renderer, origin) {
  const { segs } = section;
  const centroid = origin ?? section.centroid;

  const thinPos = new Float32Array(segs.length * 3);
  for (let i = 0; i < segs.length; i++) {
    thinPos[i * 3 + 0] = segs[i].x - centroid.x;
    thinPos[i * 3 + 1] = segs[i].y - centroid.y;
    thinPos[i * 3 + 2] = segs[i].z - centroid.z;
  }
  const gThin = new THREE.BufferGeometry();
  gThin.setAttribute('position', new THREE.BufferAttribute(thinPos, 3));
  const thin = new THREE.LineSegments(
    gThin,
    new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.001, depthTest: false }),
  );
  thin.name = SECTION_RING_PICK_NAME;
  thin.renderOrder = 9e5;

  let fat = null;
  const loop = orderLoop(segs);
  if (loop.length >= 2) {
    const fatPos = new Float32Array(loop.length * 3);
    for (let i = 0; i < loop.length; i++) {
      fatPos[i * 3 + 0] = loop[i].x - centroid.x;
      fatPos[i * 3 + 1] = loop[i].y - centroid.y;
      fatPos[i * 3 + 2] = loop[i].z - centroid.z;
    }
    const geo = new LineGeometry();
    geo.setPositions(fatPos);
    fat = new Line2(geo, ringLineMaterial(color, renderer));
    fat.computeLineDistances();
    fat.name = SECTION_RING_LINE_NAME;
    fat.renderOrder = 9e5;
    fat.frustumCulled = false;
  }

  return { thin, fat };
}

/**
 * Slice `mesh` with the horizontal plane at world-Y `y` and return a closed fat
 * ring (`Line2`) drawn at absolute world coordinates.
 *
 * Read-only counterpart to `buildSectionRingObjects`: no thin pick line, no
 * centroid re-centering and no transform group, because nothing here is
 * draggable.
 *
 * @returns {Line2|null} null when the plane misses the mesh.
 */
export function buildRingAtY(mesh, y, color, renderer) {
  const section = sliceSectionAtY(mesh, y);
  if (!section) return null;

  const loop = orderLoop(section.segs);
  if (loop.length < 2) return null;

  const positions = new Float32Array(loop.length * 3);
  for (let i = 0; i < loop.length; i++) {
    positions[i * 3 + 0] = loop[i].x;
    positions[i * 3 + 1] = loop[i].y;
    positions[i * 3 + 2] = loop[i].z;
  }

  const geo = new LineGeometry();
  geo.setPositions(positions);

  const line = new Line2(geo, ringLineMaterial(color, renderer));
  line.computeLineDistances();
  line.name = 'AppliedMeasureRing';
  line.renderOrder = 9e5;
  line.frustumCulled = false;
  return line;
}
